import { apiFetch, apiFetchBlob } from './client.js';

/**
 * Admin payments API — the transaction queue + manual execute / reject.
 * Mirrors apps/api/src/routes/admin/payments.ts.
 */

export type PaymentStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'SETTLED'
  | 'FAILED'
  | 'RETURNED'
  | 'RECALLED'
  | 'BLOCKED';

export type PaymentType = 'SEPA_CT' | 'SEPA_INSTANT' | 'SWIFT';

export interface PaymentListEntry {
  id: string;
  accountId: string;
  paymentType: PaymentType;
  direction: string;
  status: PaymentStatus;
  amountCents: string;
  currency: string;
  debtorIban: string;
  debtorName: string;
  creditorIban: string;
  creditorName: string;
  reference: string;
  failureReason: string | null;
  scheduledFor: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentHistoryEntry {
  fromStatus: string | null;
  toStatus: string;
  reason: string | null;
  actorId: string | null;
  createdAt: string;
}

export interface PaymentDetail extends PaymentListEntry {
  history: PaymentHistoryEntry[];
}

export interface PaymentListFilters {
  status?: PaymentStatus;
  paymentType?: PaymentType;
  limit?: number;
}

function toQuery(filters: Record<string, unknown>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    qs.set(k, String(v));
  }
  const str = qs.toString();
  return str === '' ? '' : `?${str}`;
}

export const paymentsApi = {
  listPayments: (filters: PaymentListFilters = {}): Promise<PaymentListEntry[]> =>
    apiFetch<PaymentListEntry[]>(`/admin/payments${toQuery(filters as Record<string, unknown>)}`),

  getPayment: (id: string): Promise<PaymentDetail> =>
    apiFetch<PaymentDetail>(`/admin/payments/${id}`),

  execute: (id: string, reason: string): Promise<PaymentDetail> =>
    apiFetch<PaymentDetail>(`/admin/payments/${id}/execute`, {
      method: 'POST',
      body: { reason },
      // Manual execution is a financial state change — send an idempotency key.
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    }),

  reject: (id: string, reason: string): Promise<PaymentDetail> =>
    apiFetch<PaymentDetail>(`/admin/payments/${id}/reject`, {
      method: 'POST',
      body: { reason },
    }),
};

/** Evidence a customer attached when they sent the payment. */
export interface PaymentDocument {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  createdAt: string;
}

export const paymentDocumentsApi = {
  list: (paymentId: string): Promise<PaymentDocument[]> =>
    apiFetch<PaymentDocument[]>(`/admin/payments/${paymentId}/documents`),

  /**
   * The bytes.
   *
   * Fetched rather than linked: the console authenticates with a bearer token,
   * which a plain <a href> would not carry — the request would arrive
   * unauthenticated and 401. Every read is logged against the operator.
   */
  download: (paymentId: string, documentId: string): Promise<Blob> =>
    apiFetchBlob(`/admin/payments/${paymentId}/documents/${documentId}`),
};
